/**
 * Extracts answer values from a comma-separated string
 *
 * @param {string} commaSeparatedAnswers - Answer string in format "answer1,answer2,answer3"
 * @returns {Array} - Array of answer values like ["answer1", "answer2", "answer3"]
 * @example
 * // Returns ["option1", "option3"]
 * extractAnswerValues("option1,option3");
 */
export const extractMultipleChoiceAnswers = (commaSeparatedAnswers) => {
  if (!commaSeparatedAnswers) return [];
  return commaSeparatedAnswers.split(',');
};

/**
 * Converts an array of selected indices into a comma-separated string of values
 *
 * @param {Array} selectedIndices - Array of selected choice indices
 * @param {Array} choices - Array of choice objects with value property
 * @returns {string} - Comma-separated string of selected values
 */
export const compactMultipleChoiceAnswers = (selectedIndices, choices) => {
  if (!selectedIndices || selectedIndices.length === 0) return '';

  return selectedIndices.map((idx) => choices[idx].value).join(',');
};

/**
 * Checks if selected answers are correct for MULTIPLE_SELECT questions
 *
 * @param {Array} selectedValues - Array of selected answer values
 * @param {Array} correctAnswers - Array of correct answer values
 * @returns {boolean} - True if all selections are correct
 */
export const isCorrectMultipleSelect = (selectedValues, correctAnswers) => {
  return (
    selectedValues.length === correctAnswers.length &&
    selectedValues.every((val) => correctAnswers.includes(val))
  );
};

/**
 * Extracts the indices of selected answers from a MULTIPLE_SELECT question
 *
 * @param {Object} answers - The answer object containing the selected values
 * @param {Array} choices - Array of choice objects with value property
 * @returns {Array} - Array of indices corresponding to the selected answers
 */
export const getAnswerIndices = (answers, choices) => {
  if (!answers) return [];

  const answerIndices = [];
  const answerValues = extractMultipleChoiceAnswers(answers);

  answerValues.forEach((val) => {
    const index = choices.findIndex((choice) => choice.value === val);
    if (index !== -1) answerIndices.push(index);
  });

  return answerIndices;
};
